import React from 'react';
import { PDFView } from '../PDFRendered';

/**
 * SubmissionViewer component for displaying student submissions
 * This component handles the display of PDF submissions
 *
 * @param {Object} props - Component properties
 * @param {string} props.submissionId - ID of the current submission
 * @param {Object} props.submissionData - The submission data object
 * @param {Array} props.examPaper - The exam paper data
 * @param {string} props.activeSubQuestionId - ID of the active subquestion (if any)
 * @param {string} props.pinnedSubQuestionId - ID of the pinned subquestion (if any)
 * @param {number} props.currentScale - Current PDF scale/zoom level
 * @param {Function} props.onScaleChange - Callback for when scale changes
 * @param {number} props.pdfViewKey - Key for forcing PDFView to reinitialize
 * @param {number} props.initialPage - Initial page to display in the PDF
 * @param {Function} props.onPageChange - Callback for when page changes
 * @param {string} props.apiUrl - API URL
 * @param {Function} props.getQuestionIdentifier - Function to get question identifier from subquestion ID
 */
const SubmissionViewer = ({
  submissionId,
  submissionData,
  examPaper,
  activeSubQuestionId,
  pinnedSubQuestionId,
  currentScale,
  onScaleChange,
  pdfViewKey,
  initialPage = 1,
  onPageChange,
  apiUrl,
  getQuestionIdentifier
}) => {
  // Only handle PDF submissions in this component
  // Essay and flexible assignments have been removed per requirements
  if (!submissionData || !submissionData.file || !submissionData.file.filename) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="bg-white p-6 rounded-lg shadow-sm text-center">
          <p className="text-lg text-gray-600">No submitted file found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col relative">
      {/* Pin badge notification with absolute positioning */}
      {pinnedSubQuestionId && (
        <div
          ref={el => {
            if (el) {
              setTimeout(() => {
                el.style.opacity = '1';
                el.style.transform = 'scale(1)';
              }, 10);
            }
          }}
          style={{
            animation: 'none',
            opacity: 0,
            transform: 'scale(0.95)',
            transition: 'all 0.3s ease-out',
            animationFillMode: 'forwards'
          }}
          className="absolute top-2 right-2 bg-[#e4b17a] text-white px-3 py-1.5 rounded-full text-sm flex items-center shadow-md z-10"
        >
          <svg
            ref={el => {
              if (el) {
                setTimeout(() => {
                  el.style.transform = 'rotate(360deg)';
                }, 10);
              }
            }}
            style={{
              animation: 'none',
              transition: 'transform 0.5s ease-in-out',
              transform: 'rotate(0deg)'
            }}
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M19 3.5l-3 2V11c0 1.2.5 2.3 1.4 3.3l.6.6V21l-6-1l-6 1v-6.1l.6-.6c.9-1 1.4-2.1 1.4-3.3V5.5l-3-2 2-2h10l2 2z" />
          </svg>
          <span className="font-medium">
            Pinned Question {getQuestionIdentifier(pinnedSubQuestionId)}
          </span>
        </div>
      )}

      {/* Animations are defined directly in the style attribute */}

      {/* PDF Viewer - Using flex-grow to fill available space */}
      <div className="flex-grow w-full" style={{ height: 'calc(100% - 10px)' }}>
        <PDFView
          key={`pdf-view-${submissionData.id}-${pdfViewKey}`}
          url={`${apiUrl}/api/pdf-file/${submissionData.file.filename}?mode=view`}
          initialPage={initialPage}
          scale={currentScale}
          onPageChange={onPageChange}
          onScaleChange={onScaleChange}
        />
      </div>
    </div>
  );
};

export default SubmissionViewer;
